import React, { useMemo, useRef, useState } from "react";

import { Tooltip } from "@mui/material";
import ShipeeziNewGrid, {
  Column,
  Constants,
  GeneralActions,
  RowActionsExtra,
} from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";
import { CgChevronDown } from "react-icons/cg";

import { StyledMenu } from "../../../../../../components/Generic/AnchorMenu";
import Breadcrumb from "../../../../../../components/Generic/Breadcrump";
import {
  CommodityCode,
  RegionCommodityCode,
  SectionCommodityCode,
} from "../../../../../../models/commodity.code";
import { Status } from "../../../../../../util/enums";
import env from "../../../../../../util/env";
import DeleteConfirmationModal, {
  DeleteConfirmationModalHandles,
} from "./components/DeleteConfirmationModal";
import ImportHSCodeModal, { ImportHSCodeModalHandles } from "./components/ImportModal";
import ManageCommodityCodeModal, {
  ManageCommodityCodeHandles,
  ManageModalMode,
} from "./components/ManageModal";
import SwitchWarningModal, { SwitchWarningModalHandles } from "./components/SwitchWarningModal";
import ViewCommodityCodeModal, { ViewCommodityCodeHandles } from "./components/ViewModal";
import useActions from "./hooks/useActions";
import {
  BadgeActive,
  BadgeInactive,
  CloneIcon,
  DeleteIcon,
  EditIcon,
  StyledButton,
  StyledButtonChevron,
  StyledButtonGroup,
  StyledMenuItem,
  StyledMenuList,
  ViewIcon,
  Wrapper,
} from "./styles";
import { CodeLevelType, CodeType, formatHSCode } from "./utils";
import { useAppState } from "Context/StateContext";
import { isShowAction } from "Util/permissions";

/**
 * This page is for HS / Commodity code maintenance
 */

type Props = {
  permission?: {
    edit: boolean;
  };
};

const HSCommodityCode = (props: Props) => {
  const { permission } = props;
  const { currentUser } = useAppState();
  const { t: translate } = useTranslation();

  const manageModalRef = useRef<ManageCommodityCodeHandles>(null);
  const switchWarningRef = useRef<SwitchWarningModalHandles>(null);
  const viewModalRef = useRef<ViewCommodityCodeHandles>(null);
  const deleteConfirmationModalRef = useRef<DeleteConfirmationModalHandles>(null);
  const importModalRef = useRef<ImportHSCodeModalHandles>(null);

  const {
    variables,
    setCurrentPage,
    setPageSize,
    handleFilter,
    handleSearch,
    handleSort,
    useDeleteCommodityCode,
    useSwitchStatusCommodityCode,
  } = useActions();

  const { mutate: deleteHSCommodityCode, isLoading: deletingCommodityCode } =
    useDeleteCommodityCode();
  const { mutate: switchCodeStatus, isLoading: loadingStatusSwitch } =
    useSwitchStatusCommodityCode();

  const badgeStatus = (status: Status) => {
    switch (status) {
      case Status.ACTIVE:
        return <BadgeActive>{status}</BadgeActive>;
      case Status.INACTIVE:
        return <BadgeInactive>{status}</BadgeInactive>;
      default:
        return <BadgeInactive>{status}</BadgeInactive>;
    }
  };

  const emptyValue = (value: string) => {
    if (!value) return "—";

    return value;
  };

  const formatApplicableTo = (regions?: RegionCommodityCode[]) => {
    if (!regions || !regions.length) return "—";

    return regions.map((region) => region.abbreviation).join(", ");
  };

  const typeOptions = useMemo(() => {
    return Object.keys(CodeLevelType).map((codeKey: string) => ({
      label: CodeLevelType[codeKey],
      value: codeKey,
    }));
  }, []);

  const sectionOptions = useMemo(() => {
    if (!variables.sections) return undefined;

    return variables.sections.map((section: SectionCommodityCode) => ({
      label: `${section.code} - ${section.description}`,
      value: `${section.id}`,
    }));
  }, [variables.sections]);

  const columns: Column[] = [
    {
      name: "hscode",
      title: translate("code"),
      formatText: (hscode: string) => formatHSCode(hscode),
      hideSort: true,
    },
    {
      name: "section",
      title: translate("section"),
      searchIdentifier: "sectionEntity.description",
      hideSearch: true,
      formatText: emptyValue,
      hideSort: true,
    },
    {
      name: "chapter",
      title: translate("chapter"),
      searchIdentifier: "chapterEntity.description",
      formatText: emptyValue,
      hideSort: true,
    },
    {
      name: "description",
      title: translate("description"),
      formatText: emptyValue,
      hideSort: true,
    },
    {
      name: "statisticalClassification",
      title: translate("statisticalClassification"),
      searchIdentifier: "statistical_classification",
      formatText: emptyValue,
      hideSort: true,
    },
    {
      name: "type",
      title: translate("type"),
      hideSearch: true,
      formatText: emptyValue,
      hideSort: true,
    },
    {
      name: "regions",
      title: translate("applicableTo"),
      hideSearch: true,
      formatText: formatApplicableTo,
      hideSort: true,
    },
    {
      name: "status",
      title: translate("status"),
      formatComponent: (status: Status) => badgeStatus(status),
      hideSearch: true,
      hideSort: true,
    },
  ];

  const [importAnchorEl, setImportAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedImportOptionIdx, setSelectedImportOptionIdx] = useState<number>(0);

  const extraOptions = () => {
    const handleImportCSV = () => {
      importModalRef?.current?.open();
    };

    const handleDownloadCSVTemplate = () => {
      window.open(env.LINK_TEMPLATE_HSCODE_CSV, "_blank")?.focus();
    };

    const menuOptions = [
      { action: handleImportCSV, label: translate("importCSV") },
      { action: handleDownloadCSVTemplate, label: translate("downloadCSVTemplate") },
    ];

    const handleImportAction = () => {
      menuOptions[selectedImportOptionIdx].action();
    };

    const handleImportOption = (optionIdx: number) => {
      setSelectedImportOptionIdx(optionIdx);
      menuOptions[optionIdx].action();
      setImportAnchorEl(null);
    };

    return (
      <div>
        <StyledButtonGroup>
          <StyledButton onClick={handleImportAction}>
            {translate(menuOptions[selectedImportOptionIdx].label)}
          </StyledButton>
          <StyledButtonChevron
            aria-controls="hscode-import-menu"
            onClick={(e) => setImportAnchorEl(e.currentTarget)}
          >
            <CgChevronDown />
          </StyledButtonChevron>
        </StyledButtonGroup>
        <StyledMenu
          anchorEl={importAnchorEl}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          keepMounted
          onClose={() => setImportAnchorEl(null)}
          open={!!importAnchorEl}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
        >
          <StyledMenuList id="hscode-import-menu">
            {menuOptions.map((opt, idx) => (
              <StyledMenuItem
                key={`hscode-import-menu-opt-${idx}`}
                onClick={() => handleImportOption(idx)}
              >
                {opt.label}
              </StyledMenuItem>
            ))}
          </StyledMenuList>
        </StyledMenu>
      </div>
    );
  };

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["handleSearch", "handleFilterBy"],
      right: ["handleActive", "handleExtraOptions", "handleNewRecord"],
    },
    handleSearch: {
      onSearch: handleSearch,
    },
    handleNewRecord: {
      text: translate("hsCommodityCode"),
      tooltip: translate("hsCommodityCode"),
      action: () => manageModalRef.current?.open(ManageModalMode.CREATE),
    },
    handleFilterBy: {
      categories: [
        ...(variables.sections
          ? [
              {
                name: translate("section"),
                column: "sectionId",
                options: sectionOptions,
              },
            ]
          : []),
        {
          name: translate("type"),
          column: "level",
          options: typeOptions,
        },
        // TODO: Add status filter
        // {
        //   name: translate("status"),
        //   column: "status",
        //   options: [
        //     { label: Status.ACTIVE, value: Status.ACTIVE },
        //     { label: Status.INACTIVE, value: Status.INACTIVE },
        //   ],
        // },
      ],
      onChange: handleFilter,
    },
    handleExtraOptions: extraOptions(),
  };

  const editCommodityCode = (commodityCode: CommodityCode) => {
    if (!commodityCode || !commodityCode?.id) return;

    manageModalRef.current?.open(ManageModalMode.UPDATE, commodityCode);
  };

  const renderEditRowButton = (_: string | number, code: CommodityCode) => {
    if (CodeLevelType[code.level] !== CodeType.COMMODITY_CODE) {
      return null;
    }

    return (
      <Tooltip data-cy="action-edit" title={translate("edit")} placement="top">
        <div>
          <EditIcon
            onClick={() => editCommodityCode(code)}
            color="#475467"
            size={Constants.defaultRowActionSize}
          />
        </div>
      </Tooltip>
    );
  };

  const viewCommodityCode = (commodityCode: CommodityCode) => {
    if (!commodityCode || !commodityCode?.id) return;

    viewModalRef.current?.open(commodityCode);
  };

  const renderViewRowButton = (_: string | number, code: CommodityCode) => {
    return (
      <Tooltip data-cy="action-edit" title={translate("view")} placement="top">
        <div>
          <ViewIcon
            onClick={() => viewCommodityCode(code)}
            color="#475467"
            size={Constants.defaultRowActionSize}
          />
        </div>
      </Tooltip>
    );
  };

  const cloneCommodityCode = (commodityCode: CommodityCode) => {
    if (!commodityCode || !commodityCode?.id) return;

    manageModalRef.current?.open(ManageModalMode.CLONE, commodityCode);
  };

  const renderCloneRowButton = (_: string | number, code: CommodityCode) => {
    if (
      CodeLevelType[code.level] !== CodeType.COMMODITY_CODE &&
      CodeLevelType[code.level] !== CodeType.HS_CODE
    ) {
      return null;
    }

    return (
      <Tooltip data-cy="action-clone" title={translate("clone")} placement="top">
        <div>
          <CloneIcon
            onClick={() => cloneCommodityCode(code)}
            color="#475467"
            size={Constants.defaultRowActionSize}
          />
        </div>
      </Tooltip>
    );
  };

  const handleChangeStatus = (id: number, code: CommodityCode) => {
    if (!id) return;

    if (
      CodeLevelType[code.level] === CodeType.CHAPTER ||
      CodeLevelType[code.level] === CodeType.HEADING
    ) {
      switchWarningRef.current?.open(code);

      return;
    }

    switchCodeStatus(id);
  };

  const handleConfirmChangeStatus = (id: number) => {
    switchCodeStatus(id);
  };

  const handleConfirmDelete = (id: number) => {
    deleteHSCommodityCode(id, {
      onSuccess: () => deleteConfirmationModalRef.current?.close(),
    });
  };

  const deleteCommodityCode = (commodityCode: CommodityCode) => {
    if (!commodityCode || !commodityCode?.id) return;

    deleteConfirmationModalRef.current?.open(commodityCode);
  };

  const renderDeleteRowButton = (_: string | number, code: CommodityCode) => {
    if (CodeLevelType[code.level] !== CodeType.COMMODITY_CODE) {
      return null;
    }

    return (
      <Tooltip data-cy="action-delete" title={translate("delete")} placement="top">
        <div>
          <DeleteIcon
            onClick={() => deleteCommodityCode(code)}
            color={"rgb(207, 42, 39)"}
            size={Constants.defaultRowActionSize}
          />
        </div>
      </Tooltip>
    );
  };

  const handleRowActionsExtraByPermission = () => {
    let actions: RowActionsExtra[] = [];

    if (permission && isShowAction(currentUser, permission?.edit)) {
      actions?.push({
        title: translate("edit"),
        key: "edit",
        buttonAndAction: renderEditRowButton,
      });
    }

    actions?.push(
      {
        title: translate("view"),
        key: "view",
        buttonAndAction: renderViewRowButton,
      },
      {
        title: translate("clone"),
        key: "clone",
        buttonAndAction: renderCloneRowButton,
      },
      {
        title: translate("delete"),
        key: "delete",
        buttonAndAction: renderDeleteRowButton,
      }
    );

    return actions;
  };

  return (
    <Wrapper data-cy="automateDataList">
      {/* <header className="mb-4">
        <Breadcrumb
          items={[
            { title: translate("configurations") },
            {
              title: translate("hsCommodityCode"),
              color: "#465869",
              dataCy: "breadcrumbsSubTitle",
            },
          ]}
        />
      </header> */}

      <ShipeeziNewGrid
        data={variables.commodityCodes}
        columns={columns}
        totalItems={variables.total}
        loadingData={variables.isFetching || loadingStatusSwitch}
        pageSize={variables.pageSize}
        currentPage={variables.page}
        emptyData={translate("noInformationToBeShown")}
        pageSizeList={[20, 40, 60, 100]}
        onChangePageSize={setPageSize}
        onNextPage={setCurrentPage}
        onPreviousPage={setCurrentPage}
        onGoToPage={setCurrentPage}
        onSorting={handleSort}
        rowActions={{
          orderBy: ["edit", "view", "handleChange", "clone", "delete"],
          handleChange: (id, row) => handleChangeStatus(Number(id), row),
        }}
        rowActionsExtra={handleRowActionsExtraByPermission()}
        positionActionsColumn="right"
        generalActions={generalActions}
      />

      <SwitchWarningModal ref={switchWarningRef} onConfirm={handleConfirmChangeStatus} />

      <ManageCommodityCodeModal ref={manageModalRef} />

      <ViewCommodityCodeModal ref={viewModalRef} />

      <DeleteConfirmationModal
        onSubmit={handleConfirmDelete}
        loadingAction={deletingCommodityCode}
        ref={deleteConfirmationModalRef}
      />

      <ImportHSCodeModal ref={importModalRef} />
    </Wrapper>
  );
};

export default HSCommodityCode;
